import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, CheckCircle } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import LoadingOverlay from '../../../components/LoadingOverlay';
import TransactionTable, { ColDef } from '../components/TransactionTable';
import { dashboardService } from '../services/dashboardService';

interface MonthOption { year: number; month: number; }
interface Summary { totalIncome: number; totalExpense: number; balance: number; count: number; }
interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  category?: string;
  groupId?: string;
  classifierId?: string;
  groupName?: string;
  classifierName?: string;
  accountId?: string;
  accountType?: string;
  excluded?: boolean;
  source?: string;
  type?: string;
  account?: { name?: string };
  [key: string]: unknown;
}

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const formatCurrency = (v: number) => Math.abs(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function getPeriodSelectValue(periodType: string, selectedMonth: MonthOption | null): string {
  if (periodType === 'month' && selectedMonth) return `${selectedMonth.year}-${selectedMonth.month}`;
  if (periodType === 'custom') return 'custom';
  return 'all';
}

const columns: ColDef[] = [
  { key: 'date', label: 'Data/Hora', getValue: r => r.date || '', render: r => { const d = new Date(r.date); return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }); } },
  { key: 'description', label: 'Descrição', getValue: r => r.description || 'Sem descrição' },
  { key: 'category', label: 'Categoria', getValue: r => r.category || 'Sem categoria', editable: true },
  { key: 'group', label: 'Agrupador', getValue: r => r.groupName || '', editable: true },
  { key: 'classifier', label: 'Classificação', getValue: r => r.classifierName || '', editable: true },
  { key: 'accountName', label: 'Conta', getValue: r => r.account?.name || '-' },
  { key: 'accountId', label: 'ID Conta', getValue: r => r.accountId || '-' },
  { key: 'accountType', label: 'Tipo Conta', getValue: r => r.accountType || '', editable: true },
  { key: 'source', label: 'Origem', getValue: r => r.source === 'sync' ? 'Sync' : 'Manual', render: r => <span className={r.source === 'manual' ? 'text-red-600 font-medium' : 'text-green-600'}>{r.source === 'sync' ? 'Sync' : 'Manual'}</span> },
  { key: 'type', label: 'Tipo', getValue: r => r.type === 'CREDIT' ? 'Receita' : 'Despesa' },
  { key: 'excluded', label: 'Desc.', getValue: r => r.excluded ? 'Sim' : 'Não', render: r => <input type="checkbox" checked={!!r.excluded} readOnly className="rounded pointer-events-none" /> },
  {
    key: 'amount', label: 'Valor', isNumeric: true,
    getValue: r => {
      const v = (r.adjustedAmount !== undefined && r.adjustedAmount !== null) ? Number(r.adjustedAmount) : Number(r.amount);
      return v.toFixed(2);
    },
    render: r => {
      const hasAdjusted = r.adjustedAmount !== undefined && r.adjustedAmount !== null;
      const displayVal = hasAdjusted ? Number(r.adjustedAmount) : Number(r.amount);
      const isPositive = displayVal >= 0;
      return (
        <span className="inline-flex flex-col items-end leading-tight">
          <span
            className={`font-semibold ${hasAdjusted ? (isPositive ? 'text-blue-600' : 'text-amber-600') : (isPositive ? 'text-green-600' : 'text-red-600')}`}
            title={hasAdjusted ? `Original: ${formatCurrency(Number(r.amount))}` : undefined}
          >
            {formatCurrency(displayVal)}{hasAdjusted ? ' *' : ''}
          </span>
          {hasAdjusted && (
            <span className="text-[10px] text-gray-400 leading-none">
              orig: {formatCurrency(Number(r.amount))}
            </span>
          )}
        </span>
      );
    },
  },
];

const HomePage: React.FC = () => {
  const [months, setMonths] = useState<MonthOption[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(() => {
    try {
      const s = localStorage.getItem('fin_period');
      if (s) {
        const p = JSON.parse(s);
        if (p.type === 'month') return p.value;
        if (p.type === 'all' || p.type === 'custom') return null;
      }
    } catch { /* ignore */ }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [customRange, setCustomRange] = useState<{ start: string; end: string }>(() => {
    try { const s = localStorage.getItem('fin_period'); if (s) { const p = JSON.parse(s); if (p.type === 'custom') return p.value; } } catch {} return { start: '', end: '' };
  });
  const [periodType, setPeriodType] = useState<'month' | 'all' | 'custom'>(() => {
    try { const s = localStorage.getItem('fin_period'); if (s) return JSON.parse(s).type; } catch {} return 'month';
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState<'todos' | 'receitas_despesas' | 'gastos' | 'credito'>('todos');
  const [classifierOptions, setClassifierOptions] = useState<{_id: string; name: string; groupId: string}[]>([]);
  const [groupOptions, setGroupOptions] = useState<{_id: string; name: string}[]>([]);
  const accountTypeOptions = ['CREDITO', 'DEBITO'];

  const filteredTransactions = useMemo(() => {
    if (viewMode === 'receitas_despesas') return transactions.filter(t => (t.accountType || '').toLowerCase() !== 'credito');
    if (viewMode === 'gastos') return transactions.filter(t => (t.groupName || '').toLowerCase() !== 'salario');
    if (viewMode === 'credito') return transactions.filter(t => (t.accountType || '').toLowerCase() === 'credito');
    return transactions;
  }, [transactions, viewMode]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const localSummary = useMemo(() => {
    const source = selectedIds.length > 0 ? filteredTransactions.filter(t => selectedIds.includes(t.id)) : filteredTransactions;
    let totalIncome = 0, totalExpense = 0;
    for (const t of source) {
      if (t.excluded) continue;
      const amt = Number(t.amount) || 0;
      if (amt >= 0) totalIncome += amt;
      else totalExpense += Math.abs(amt);
    }
    return { totalIncome, totalExpense, balance: totalIncome - totalExpense, count: source.length };
  }, [filteredTransactions, selectedIds]);

  useEffect(() => {
    if (periodType === 'month' && selectedMonth) localStorage.setItem('fin_period', JSON.stringify({ type: 'month', value: selectedMonth }));
    else if (periodType === 'custom') localStorage.setItem('fin_period', JSON.stringify({ type: 'custom', value: customRange }));
    else localStorage.setItem('fin_period', JSON.stringify({ type: 'all' }));
  }, [periodType, selectedMonth, customRange]);

  const initialLoadDone = React.useRef(false);

  const loadClassifications = useCallback(async () => {
    const [grpRes, clsRes] = await Promise.all([
      dashboardService.getGroups(),
      dashboardService.getClassifiers(),
    ]);
    setGroupOptions(grpRes.data.data);
    setClassifierOptions(clsRes.data.data);
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params: { year?: number; month?: number; startDate?: string; endDate?: string } = {};
      if (selectedMonth) { params.year = selectedMonth.year; params.month = selectedMonth.month; }
      else if (customRange.start && customRange.end) { params.startDate = customRange.start; params.endDate = customRange.end; }

      const res = await dashboardService.getDashboard(params);
      const d = res.data.data;
      setTransactions(d.transactions);
      if (d.months) setMonths(d.months);
      if (!initialLoadDone.current && !selectedMonth && periodType === 'month' && d.months?.length) {
        initialLoadDone.current = true;
        setSelectedMonth(d.months[0]);
        return;
      }
      initialLoadDone.current = true;
    } finally { setLoading(false); }
  }, [selectedMonth, customRange, periodType]); // eslint-disable-line

  const [saving, setSaving] = useState(false);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResults, setAiResults] = useState<{ fileUrl: string; transactions: Array<{ id: string; description: string; amount: number; date: string; type: string }> } | null>(null);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadClassifications(); }, [loadClassifications]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiProcessing(true);
    setAiResults(null);
    try {
      const res = await dashboardService.createFromImage(file);
      setAiResults(res.data.data);
      await loadDashboard();
    } finally {
      setAiProcessing(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleBatchEdit = async (ids: string[], field: string, value: string | boolean) => {
    setSaving(true);
    // Map column keys to backend field names
    const fieldMap: Record<string, string> = { group: 'groupId', classifier: 'classifierId' };
    const backendField = fieldMap[field] || field;
    // adjustedAmount: string vazia = null, string numérica = number
    const backendValue = field === 'adjustedAmount'
      ? (typeof value === 'string' && value.trim() !== '' ? Number(value) : null)
      : value;
    await dashboardService.batchUpdate(ids, { [backendField]: backendValue });
    // Update local state with the name for display
    const nameUpdates: Record<string, string | number | boolean | null> = {};
    if (field === 'group') {
      nameUpdates.groupId = value as string;
      nameUpdates.groupName = groupOptions.find(a => a._id === value)?.name || '';
    } else if (field === 'classifier') {
      nameUpdates.classifierId = value as string;
      nameUpdates.classifierName = classifierOptions.find(c => c._id === value)?.name || '';
    } else if (field === 'excluded') {
      nameUpdates.excluded = value as boolean;
      if (value) { nameUpdates.groupId = ''; nameUpdates.classifierId = ''; nameUpdates.groupName = ''; nameUpdates.classifierName = ''; }
    } else if (field === 'adjustedAmount') {
      nameUpdates.adjustedAmount = backendValue as number | null;
    } else {
      nameUpdates[field] = field === 'amount' ? Number(value) : value;
    }
    setTransactions(prev => prev.map(t => ids.includes(t.id) ? { ...t, ...nameUpdates } : t));
    if (field === 'group' || field === 'classifier') loadClassifications();
    setSaving(false);
  };

  const handleInvertAmount = async (ids: string[]) => {
    setSaving(true);
    await dashboardService.invertAmount(ids);
    setTransactions(prev => prev.map(t => ids.includes(t.id) ? { ...t, amount: -t.amount } : t));
    setSaving(false);
  };

  const handleCellEdit = async (id: string, field: string, value: string | boolean) => {
    const fieldMap: Record<string, string> = { group: 'groupId', classifier: 'classifierId' };
    const backendField = fieldMap[field] || field;
    await dashboardService.batchUpdate([id], { [backendField]: value });
    const nameUpdates: Record<string, string | number | boolean> = {};
    if (field === 'group') {
      nameUpdates.groupId = value as string;
      nameUpdates.groupName = groupOptions.find(a => a._id === value)?.name || '';
    } else if (field === 'classifier') {
      nameUpdates.classifierId = value as string;
      nameUpdates.classifierName = classifierOptions.find(c => c._id === value)?.name || '';
    } else if (field === 'excluded') {
      nameUpdates.excluded = value as boolean;
      if (value) { nameUpdates.groupId = ''; nameUpdates.classifierId = ''; nameUpdates.groupName = ''; nameUpdates.classifierName = ''; }
    } else {
      nameUpdates[field] = value;
    }
    setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...nameUpdates } : t));
  };

  const handleDelete = async (id: string) => {
    await dashboardService.deleteTransaction(id);
    setTransactions(prev => prev.filter(t => t.id !== id));
  };

  if (loading && !transactions.length) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Dashboard Financeiro" />

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b flex flex-col sm:flex-row gap-3 flex-wrap">
        <select
          value={getPeriodSelectValue(periodType, selectedMonth)}
          onChange={e => {
            if (e.target.value === 'all') { setPeriodType('all'); setSelectedMonth(null); setCustomRange({ start: '', end: '' }); }
            else if (e.target.value === 'custom') { setPeriodType('custom'); setSelectedMonth(null); }
            else { const [y, m] = e.target.value.split('-'); setPeriodType('month'); setSelectedMonth({ year: +y, month: +m }); setCustomRange({ start: '', end: '' }); }
          }}
          className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto"
        >
          <option value="all">Todo período</option>
          <option value="custom">Período personalizado</option>
          {months.map(m => (
            <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
              {MONTH_NAMES[m.month - 1]} {m.year}
            </option>
          ))}
        </select>

        {periodType === 'custom' && (
          <>
            <input type="date" value={customRange.start} onChange={e => setCustomRange(p => ({ ...p, start: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto" />
            <input type="date" value={customRange.end} onChange={e => setCustomRange(p => ({ ...p, end: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto" />
          </>
        )}

        <select value={viewMode} onChange={e => setViewMode(e.target.value as 'todos' | 'receitas_despesas' | 'gastos' | 'credito')} className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto">
          <option value="todos">Todos</option>
          <option value="receitas_despesas">Receitas x Despesas</option>
          <option value="gastos">Gastos</option>
          <option value="credito">Crédito</option>
        </select>
      </div>

      <LoadingOverlay isVisible={loading && transactions.length > 0} message="Carregando transações..." />
      {!loading || transactions.length > 0 ? (
        <div className="p-4">
          {/* Summary Cards */}
          {localSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700">Receitas</p>
                <p className="text-xl font-bold text-green-800">{formatCurrency(localSummary.totalIncome)}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">Despesas</p>
                <p className="text-xl font-bold text-red-800">{formatCurrency(localSummary.totalExpense)}</p>
              </div>
              <div className={`border rounded-lg p-4 ${localSummary.balance >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                <p className="text-sm text-gray-700">Saldo</p>
                <p className={`text-xl font-bold ${localSummary.balance >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>{formatCurrency(localSummary.balance)}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">Transações</p>
                <p className="text-xl font-bold text-gray-800">{localSummary.count}</p>
              </div>
            </div>
          )}

          {/* Transaction Table */}
          <TransactionTable rows={filteredTransactions} columns={columns} onBatchEdit={handleBatchEdit} onInvertAmount={handleInvertAmount} onCellEdit={handleCellEdit} onDelete={handleDelete} classifierOptions={classifierOptions.map(c => ({ id: c._id, name: c.name, groupId: c.groupId }))} groupOptions={groupOptions.map(a => ({ id: a._id, name: a.name }))} accountTypeOptions={accountTypeOptions} onSelectionChange={setSelectedIds}
            toolbarLeft={
              <div className="space-y-2 w-full sm:w-auto">
                <div className="flex gap-2">
                  <button onClick={() => setShowCreateModal(true)} className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"><Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nova</span></button>
                  <input ref={imageInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageUpload} />
                </div>
                {aiProcessing && (
                  <div className="flex items-center gap-2 text-sm text-blue-600 animate-pulse">
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-500 animate-spin" /> Processando com IA...
                  </div>
                )}
                {aiResults && (
                  <div className="border rounded-lg bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Transações identificadas</span>
                      <button onClick={() => setAiResults(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                    </div>
                    {aiResults.transactions.length === 0 && (
                      <p className="text-xs text-gray-500">Nenhuma transação identificada no arquivo.</p>
                    )}
                    {aiResults.transactions.length > 0 && aiResults.transactions[0]?.id === 'error' && (
                      <p className="text-xs text-red-600">{aiResults.transactions[0].description}</p>
                    )}
                    {aiResults.transactions.length > 0 && aiResults.transactions[0]?.id !== 'error' && (
                      <div className="space-y-1">
                        {aiResults.transactions.map(tx => (
                          <div key={tx.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1.5 border">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-gray-800 truncate block">{tx.description}</span>
                              <span className="text-gray-400">{new Date(tx.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</span>
                            </div>
                            <span className={`shrink-0 ml-2 font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {Math.abs(tx.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            }
          />
        </div>
      ) : <LoadingSpinner />}

      {showCreateModal && (
        <CreateTransactionModal
          groupOptions={groupOptions}
          classifierOptions={classifierOptions}
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            setSaving(true);
            setShowCreateModal(false);
            await dashboardService.createTransaction(data);
            await loadDashboard();
            setSaving(false);
          }}
        />
      )}

      <LoadingOverlay isVisible={saving} message="Atualizando transações..." />
    </>
  );
};

export default HomePage;

// === Create Transaction Modal ===
const CreateTransactionModal: React.FC<{
  groupOptions: {_id: string; name: string}[];
  classifierOptions: {_id: string; name: string; groupId: string}[];
  onClose: () => void;
  onSave: (data: { description: string; amount: number; date: string; category?: string; groupId?: string; classifierId?: string; accountType?: string }) => void;
}> = ({ groupOptions, classifierOptions, onClose, onSave }) => {
  const [date, setDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [tipo, setTipo] = useState<'despesa' | 'receita'>('despesa');
  const [groupId, setGroupId] = useState('');
  const [classifierId, setClassifierId] = useState('');
  const [accountType, setAccountType] = useState('');
  const filteredCls = groupId ? classifierOptions.filter(c => c.groupId === groupId) : classifierOptions;

  const handleSubmit = () => {
    if (!description.trim() || !amount || !date) return;
    const value = Math.abs(parseFloat(amount));
    const finalAmount = tipo === 'despesa' ? -value : value;
    onSave({ description: description.trim(), amount: finalAmount, date: date + ':00.000Z', groupId: groupId || undefined, classifierId: classifierId || undefined, accountType: accountType || undefined });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full sm:max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="font-semibold text-gray-900 text-lg">Nova Transação</h3>
        <div className="space-y-3">
          <div>
            <label htmlFor="tx-date" className="block text-xs font-medium text-gray-700 mb-1">Data/Hora *</label>
            <input id="tx-date" type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div>
            <label htmlFor="tx-desc" className="block text-xs font-medium text-gray-700 mb-1">Descrição *</label>
            <input id="tx-desc" autoFocus value={description} onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="tx-amount" className="block text-xs font-medium text-gray-700 mb-1">Valor *</label>
              <input id="tx-amount" type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            </div>
            <div>
              <span className="block text-xs font-medium text-gray-700 mb-1">Tipo *</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button type="button" onClick={() => setTipo('despesa')}
                  className={`flex-1 py-2 text-sm font-medium ${tipo === 'despesa' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  Despesa
                </button>
                <button type="button" onClick={() => setTipo('receita')}
                  className={`flex-1 py-2 text-sm font-medium ${tipo === 'receita' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  Receita
                </button>
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="tx-group" className="block text-xs font-medium text-gray-700 mb-1">Agrupador</label>
            <select id="tx-group" value={groupId} onChange={e => { setGroupId(e.target.value); setClassifierId(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">— Nenhum —</option>
              {groupOptions.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tx-classifier" className="block text-xs font-medium text-gray-700 mb-1">Classificação</label>
            <select id="tx-classifier" value={classifierId} onChange={e => setClassifierId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">— Nenhuma —</option>
              {filteredCls.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="tx-account-type" className="block text-xs font-medium text-gray-700 mb-1">Tipo de Conta</label>
            <select id="tx-account-type" value={accountType} onChange={e => setAccountType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">— Nenhum —</option>
              <option value="DEBITO">Débito</option>
              <option value="CREDITO">Crédito</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={!description.trim() || !amount || !date}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Salvar</button>
        </div>
      </div>
    </div>
  );
};
